import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { ActionsService } from 'src/app/shared/services/actions.service';
import { BinanceService } from '../../services/binance.service';
import { CartolaService } from '../../services/cartola.service';

import { Router } from '@angular/router';
import { ParametrosEnum } from 'src/app/shared/enums/parameters.enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { BeneficiarioZelle } from '../../interfaces/beneficiario-zelle.interface';
import { HoursOperationService } from '../../services/hours-operation.service';
import { ParameterService } from '../../services/parameter.service';
import { BeneficiaryZelleComponent } from './components/beneficiary-zelle/beneficiary-zelle.component';
import { CalculatorZelleCalComponent } from './components/calculator-zelle-cal/calculator-zelle-cal.component';

@Component({
  selector: 'app-calculator-zelle',
  templateUrl: './calculator-zelle.component.html',
  styleUrls: ['./calculator-zelle.component.sass'],
})
export class CalculatorZelleComponent implements OnInit {
  isVertical = false;
  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    if (window.innerWidth <= 768) {
      this.isVertical = true;
    } else {
      this.isVertical = false;
    }
  }
  constructor(
    private cartolaService: CartolaService,
    private store: Store<AppState>,
    private binanceService: BinanceService,
    private actionService: ActionsService,
    private hourOperations: HoursOperationService,
    private router: Router,
    private spinner: SpinnerService,
    private parameterService: ParameterService
  ) {}

  @ViewChild('beneficiarioComponent') beneficiaryComponent:
    | BeneficiaryZelleComponent
    | undefined;

  @ViewChild('calculatorComponent') calculatorComponent:
    | CalculatorZelleCalComponent
    | undefined;

  public subscription: Subscription = new Subscription();
  public user: any;
  public saldoActual = 0;
  public sobregiroDisponible: number | undefined = 0;
  public saldoContable: number | undefined = 0;
  public lineaCreditoBloqueada: boolean | undefined = false;
  public lineaCredito: number | undefined = 0;
  public hasLoaded: boolean = false;
  public usdt_clp_tax: number = 0;
  public stepPass2: boolean = false;
  public stepPass3: boolean = false;
  public stepPass4: boolean = false;
  public clpCurrency: number = 0;
  public zelleCurrency: number = 0;
  public beneficiario: BeneficiarioZelle | undefined;
  public valorCambioId: number = 0;
  public zelleSellValue: number = 0;
  public zelleBuyValue: number = 0;
  public porCompZelle: number = 0;
  public descVentaZelle: number = 0;
  public spotPrice: number = 0;
  public limitSellZelle: number = 0;
  estadoOperaciones = true;
  estadoStopOperacion = true;
  currentStep = 1;
  totalSteps = 3;
  slideDirection = '';
  isTransitioning = false;
  ngOnInit(): void {
    this.onWindowResize();
    this.subscription.add(
      this.actionService.subjectTasaUSDT.subscribe((res) => {
        this.getCurrentValues();
        if (this.currentStep !== 1) {
          Swal.fire(
            'Tasas actualizadas',
            'Se han actualizado las tasas, será regresado al primer paso',
            'warning'
          ).then(() => {});
          this.currentStep = 1;
        }
      })
    );
    this.subscription.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.user = user;
        this.obtenerSaldo();
      })
    );
    this.getCurrentValues();
    this.validarEstado();
  }

  async validarEstado() {
    this.estadoOperaciones = await this.hourOperations.getEstadoOperacion(
      'ZELLE'
    );
    this.estadoStopOperacion = await this.hourOperations.getEstadoOperacion(
      'ZELLE_STOP'
    );
    const message =
      'Hemos cerrado operaciones, muchas gracias por preferirnos.';
    if (!this.estadoOperaciones) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        title: 'Operación cerrada',
        text: message,
      });
      this.router.navigate(['/wallet']);
    }
    if (!this.estadoStopOperacion) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        title: 'Operación Pausada',
        text: 'Por motivos operativos, el sistema estará en pausa durante 10 minutos.',
      });
      this.router.navigate(['/wallet']);
    }
  }

  /**
   * 30/10/2026: se le pasa al resumen para volver a consultar el canal justo
   * antes de confirmar. `validarEstado` avisa y saca al cliente si está cerrado
   * o en pausa. Si la consulta falla no se bloquea, igual que al entrar.
   */
  operacionSigueAbierta = async (): Promise<boolean> => {
    try {
      await this.validarEstado();
    } catch {
      return true;
    }
    return !!this.estadoOperaciones && !!this.estadoStopOperacion;
  };

  async obtenerSaldo() {
    if (this.user?.usuarioId) {
      const {
        saldo,
        lineaCreditoId,
        balancePermitido,
        lineaBloqueada,
        sobregiro,
      } = await this.cartolaService.getSaldoCliente(this.user.usuarioId);
      this.saldoActual = saldo;
      this.sobregiroDisponible = sobregiro;
      this.saldoContable = balancePermitido;
      this.lineaCreditoBloqueada = lineaBloqueada;
      this.lineaCredito = lineaCreditoId;
    }
  }

  async getCurrentValues() {
    const ref = this.spinner.start();
    const currentValues = await this.binanceService.getConversionData();
    this.usdt_clp_tax = currentValues.tasaUsdtClp;
    this.valorCambioId = currentValues.id;
    await this.parameterService
      .getParameter(ParametrosEnum.DESCUENTO_COM_ZELLE)
      .then((value) => {
        this.descVentaZelle = value.valor;
      });
    await this.parameterService
      .getParameter(ParametrosEnum.PRECIO_SPOT)
      .then((value) => {
        this.spotPrice = value.valor;
      });
    await this.parameterService
      .getParameter(ParametrosEnum.PORC_VENTA_ZELLE)
      .then((value) => {
        this.porCompZelle = value.valor;
      });
    this.zelleSellValue = +this.usdt_clp_tax * +this.porCompZelle;
    this.zelleBuyValue = +this.spotPrice - +this.descVentaZelle;

    this.zelleSellValue = Math.round(this.zelleSellValue * 100) / 100;
    this.zelleBuyValue = Math.round(this.zelleBuyValue * 100) / 100;
    this.spinner.stop(ref);
  }

  async returngetCurrentTax() {
    const currentValues = await this.binanceService.getConversionData();
    if (
      currentValues.tasaUsdtClp != this.usdt_clp_tax &&
      this.usdt_clp_tax != 0
    ) {
      this.getCurrentValues();
      if (this.currentStep !== 1) {
        Swal.fire(
          'Tasas actualizadas',
          'Se han actualizado las tasas, será regresado al primer paso',
          'warning'
        ).then(() => {});
        this.currentStep = 1;
        setTimeout(
          () => this.calculatorComponent!.transformCurrency('clp'),
          1000
        );
      }
    }
  }

  async validateGetCurrentValues() {
    const currentValues = await this.binanceService.getConversionData();
    let last_tax = this.usdt_clp_tax;
    if (last_tax != currentValues.tasaUsdtClp) {
    }
    this.usdt_clp_tax = currentValues.tasaUsdtClp;
    this.valorCambioId = currentValues.id;
  }

  nextStep(step: number): void {
    switch (step) {
      case 2: {
        this.stepPass2 = true;
        break;
      }
      case 3: {
        this.stepPass3 = true;
        break;
      }
      case 4: {
        this.stepPass4 = true;
        break;
      }
    }
    this.slideDirection = 'left';
    this.currentStep = step;
  }

  previousStep(): void {
    if (this.currentStep === 1) {
      this.router.navigate(['/wallet/client/zelle-home']);
      return;
    }
    this.slideDirection = 'right';
    this.currentStep--;
  }

  assignClp(clp: number) {
    this.clpCurrency = clp;
    if (this.clpCurrency == 0) this.stepPass3 = false;
  }

  assignZelle(usd: number) {
    this.zelleCurrency = usd;
    if (this.zelleCurrency == 0) this.stepPass3 = false;
  }

  assignBeneficiario(beneficiario: BeneficiarioZelle) {
    this.beneficiario = beneficiario;
    // Límite unificado — todos los bancos admiten hasta 10.000 USD por operación
    this.limitSellZelle = 10000;
  }

}
